import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { isValidISOCode } from "../../helper";
import {
  INDIAN_STATES,
  DIAL_CODES,
  COUNTRIES,
  splitContact,
  PHONE_CODE_OPTIONS,
  isIndia,
  joinContact
} from "../../geo";
import api from "../../api";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Divider,
  FormControlLabel,
  Switch,
  CircularProgress,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  InputAdornment,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { FilePond, registerPlugin } from "react-filepond";
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";

import SnackBarComponent from "../../components/SnackBarComponent";
registerPlugin(FilePondPluginImagePreview);


export default function MasterForm() {
  const { service_code, country_code } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(service_code && country_code);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [originalData, setOriginalData] = useState({});

  const [formData, setFormData] = useState({
    country_code: "",
    service_code: "",
    name: "",
    type: "",

    address: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    landmark: "",
    latitude: "",
    longitude: "",
    directions_link: "",

    // UI-only: recombined into `contact` on submit, never sent on its own
    phone_code: "",
    contact: "",
    email: "",

    visible: true,
    operational: true,
  });

  const [imageLinks, setImageLinks] = useState([""]);
  const [imagePreviewLinks, setImagePreviewLinks] = useState([""]);
  const [imageFiles, setImageFiles] = useState([]);
  const [operatingSchedules, setOperatingSchedules] = useState([{ days: "", hours: "" }]);
  const [coordinateHelpOpen, setCoordinateHelpOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    if (isEditing) {
      const fetchData = async () => {
        setIsLoading(true);
        const token = localStorage.getItem("accessToken");
        try {
          const response = await api.get("/api/service/all", {
            headers: { Authorization: `Bearer ${token}` },
          });

          const item = response.data.data.find(
            (r) => r.service_code === service_code && r.country_code === country_code
          );

          if (item) {
            // Restore formData except custom arrays
            const { image_link, operating_days, ...rest } = item;
            const { dial, number } = splitContact(item.contact);
            setFormData((prev) => ({
              ...prev,
              ...rest,
              // Fall back to the centre's own country when the stored number
              // carries no dial code yet
              phone_code: dial || (DIAL_CODES[item.country_code] ? `+${DIAL_CODES[item.country_code]}` : ""),
              contact: number
            }));
            setOriginalData(item);

            if (image_link && typeof image_link === "object") {
              const links = Array.isArray(image_link) ? image_link : Object.values(image_link);
              if (links.length > 0) {
                setImageLinks(links);
                setImagePreviewLinks(links);
              }
            }
            if (operating_days && typeof operating_days === "object") {
              let schedules = [];
              if (operating_days.open_hours && Array.isArray(operating_days.open_hours)) {
                schedules = operating_days.open_hours.map(item => {
                  const day = Object.keys(item)[0];
                  return { days: day || "", hours: item[day] || "" };
                });
              } else if (Array.isArray(operating_days)) {
                schedules = operating_days.map(d => ({ days: d.days || "", hours: d.timings || d.hours || "" }));
              } else {
                schedules = Object.entries(operating_days).map(([days, hours]) => ({ days, hours }));
              }
              if (schedules.length > 0) setOperatingSchedules(schedules);
            }
          }
        } catch (error) {
          console.error("Error fetching data", error);
          setSnackbar({ open: true, message: "Failed to load data", severity: "error" });
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [service_code, country_code, isEditing]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    if (name === "country_code") {
      finalValue = value.toUpperCase();
    } else if (["state", "country", "city"].includes(name)) {
      finalValue = value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
    }

    setFormData({ ...formData, [name]: finalValue });
  };

  // Country drives country_code and the phone code, so they can never disagree.
  // country_code is half the primary key, which is why the field is locked while
  // editing.
  const handleCountryChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      country: selected ? selected.name : "",
      country_code: selected ? selected.code : prev.country_code,
      phone_code: selected ? selected.dial : prev.phone_code,
    }));
  };

  const handleSwitchChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };

  const isValidUrl = (string) => {
    if (!string) return true;
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleAddImageLink = () => {
    setImageLinks([...imageLinks, ""]);
    setImagePreviewLinks([...imagePreviewLinks, ""]);
  };
  const handleRemoveImageLink = (index) => {
    const newLinks = [...imageLinks];
    const newPreviewLinks = [...imagePreviewLinks];
    newLinks.splice(index, 1);
    newPreviewLinks.splice(index, 1);
    setImageLinks(newLinks.length ? newLinks : [""]);
    setImagePreviewLinks(newPreviewLinks.length ? newPreviewLinks : [""]);
  };
  const handleImageLinkChange = (index, value) => {
    const newLinks = [...imageLinks];
    newLinks[index] = value;
    setImageLinks(newLinks);

    const trimmedValue = value.trim();
    const newPreviewLinks = [...imagePreviewLinks];
    if (!trimmedValue) {
      newPreviewLinks[index] = "";
    } else if (isValidUrl(trimmedValue)) {
      newPreviewLinks[index] = trimmedValue;
    }
    setImagePreviewLinks(newPreviewLinks);
  };

  const handleAddSchedule = () => setOperatingSchedules([...operatingSchedules, { days: "", hours: "" }]);
  const handleRemoveSchedule = (index) => {
    const newSchedules = [...operatingSchedules];
    newSchedules.splice(index, 1);
    setOperatingSchedules(newSchedules.length ? newSchedules : [{ days: "", hours: "" }]);
  };
  const handleScheduleChange = (index, field, value) => {
    const newSchedules = [...operatingSchedules];
    newSchedules[index][field] = value;
    setOperatingSchedules(newSchedules);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    if (formData.country_code.length !== 2) {
      setSnackbar({ open: true, message: "Country Code must be exactly 2 characters.", severity: "warning" });
      setIsSaving(false);
      return;
    }
    if (!isValidISOCode(formData.country_code)) {
      setSnackbar({
        open: true,
        message: "Invalid Country Code. Please enter a valid 2-letter ISO code.",
        severity: "warning"
      });
      setIsSaving(false);
      return;
    }
    // Decimal degrees only — the backend parseFloat's these straight into the
    // record, so a DMS string would silently store as 21.
    const coordError = (label, value, limit) => {
      const text = (value ?? "").toString();
      if (!text) return null;
      if (text !== text.trim()) return `${label} must not have leading or trailing spaces.`;
      if (!/^-?\d+\.\d{4}$/.test(text)) return `${label} must have exactly 4 decimal places, e.g. 21.1212.`;
      return Math.abs(parseFloat(text)) > limit ? `${label} must be between -${limit} and ${limit}.` : null;
    };

    const coordMessage =
      coordError("Latitude", formData.latitude, 90) ||
      coordError("Longitude", formData.longitude, 180);
    if (coordMessage) {
      setSnackbar({ open: true, message: coordMessage, severity: "warning" });
      setIsSaving(false);
      return;
    }

    if (formData.directions_link && !isValidUrl(formData.directions_link)) {
      setSnackbar({ open: true, message: "Invalid Directions Link.", severity: "warning" });
      setIsSaving(false);
      return;
    }

    // Prepare imageLinks array by removing empty items
    const image_link = imageLinks.filter(link => link.trim() !== "");

    // Convert operatingSchedules array to array of objects
    const open_hours = [];
    operatingSchedules.forEach((schedule) => {
      if (schedule.days.trim() && schedule.hours.trim()) {
        open_hours.push({
          [schedule.days.trim()]: schedule.hours.trim()
        });
      }
    });

    const operating_days = { open_hours };

    const p = new FormData();
    Object.keys(formData).forEach(key => {
      // phone_code is a UI-only split of contact, recombined below
      if (key === 'phone_code') return;
      else if (key === 'contact') p.append(key, joinContact(formData.phone_code, formData.contact));
      else if (['operational', 'visible'].includes(key)) p.append(key, formData[key].toString());
      else p.append(key, formData[key] === null ? "" : formData[key]);
    });

    // Pass operating_days as JSON
    p.append("operating_days", JSON.stringify(operating_days));

    if (image_link.length > 0) {
      p.append("imageUrls", JSON.stringify(image_link));
    }

    // Pass original image links to help backend detect deletions
    if (originalData.image_link) {
      const origLinks = Array.isArray(originalData.image_link)
        ? originalData.image_link
        : Object.values(originalData.image_link);
      p.append("old_image_links", JSON.stringify(origLinks));
    }

    // Append new uploaded files
    imageFiles.forEach(fileItem => {
      if (fileItem.file) {
        p.append("imageFiles", fileItem.file);
      }
    });

    const token = localStorage.getItem("accessToken");
    try {
      if (isEditing) {
        await api.put("/api/service/update", p, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await api.post("/api/service/create", p, { headers: { Authorization: `Bearer ${token}` } });
      }

      setSnackbar({
        open: true,
        message: isEditing ? "Updated Successfully!" : "Created Successfully!",
        severity: "success"
      });

      setTimeout(() => navigate("/service/service-centre-master"), 1000);
    } catch (error) {
      console.error("Error saving data", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.response?.data?.error || "Failed to save",
        severity: "error"
      });
      setIsSaving(false);
    }
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", p: 2 }}>
      {snackbar.open && (
        <SnackBarComponent
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        />
      )}

      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/service/service-centre-master")} sx={{ mb: 2 }}>
        Back to List
      </Button>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          {isEditing ? "Edit Service Centre" : "Add New Service Centre"}
        </Typography>

        <form onSubmit={handleSubmit}>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 2, fontWeight: 'bold' }}>IDENTITY</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField label="Service Code" name="service_code" value={formData.service_code} onChange={handleInputChange} disabled={isEditing} required fullWidth />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Country Code (2 Char)"
                name="country_code"
                value={formData.country_code}
                disabled
                required
                fullWidth
                helperText="Set automatically from the Country you pick below."
              />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Name" name="name" value={formData.name} onChange={handleInputChange} required fullWidth />
            </Grid>

            {/* TYPE DROPDOWN */}
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select name="type" value={formData.type} label="Type" onChange={handleInputChange}>
                  <MenuItem value="COCO">COCO</MenuItem>
                  <MenuItem value="Dealer">Dealer</MenuItem>
                  {!["COCO", "Dealer"].includes(formData.type) && formData.type && (<MenuItem value={formData.type}>{formData.type}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* LOCATION */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 'bold' }}>LOCATION</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField label="Address" name="address" value={formData.address} onChange={handleInputChange} multiline rows={2} fullWidth required /></Grid>
            <Grid item xs={4}><TextField label="City" name="city" value={formData.city} onChange={handleInputChange} fullWidth required /></Grid>
            <Grid item xs={4}>
              {isIndia(formData) ? (
                <FormControl fullWidth required>
                  <InputLabel id="state-label">State</InputLabel>
                  {/* Not handleInputChange — its title-casing would mangle names like
                      "Andaman and Nicobar Islands" and break the match with the list */}
                  <Select
                    labelId="state-label"
                    name="state"
                    value={formData.state}
                    label="State"
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  >
                    {INDIAN_STATES.map((state) => (
                      <MenuItem key={state} value={state}>{state}</MenuItem>
                    ))}
                    {/* Keep an existing value that predates this list selectable */}
                    {formData.state && !INDIAN_STATES.includes(formData.state) && (
                      <MenuItem value={formData.state}>{formData.state}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              ) : (
                /* No curated subdivision list outside India, so this stays
                   optional free text */
                <TextField label="State (optional)" name="state" value={formData.state} onChange={handleInputChange} fullWidth />
              )}
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                options={COUNTRIES}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                value={COUNTRIES.find((c) => c.name === formData.country) || null}
                onChange={(event, selected) => handleCountryChange(selected)}
                disabled={isEditing}
                renderInput={(params) => (
                  <TextField {...params} label="Country" required fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={4}><TextField label="Pincode" name="pincode" value={formData.pincode} onChange={handleInputChange} fullWidth required /></Grid>
            <Grid item xs={8}><TextField label="Landmark" name="landmark" value={formData.landmark} onChange={handleInputChange} fullWidth /></Grid>
            <Grid item xs={6}>
              <TextField
                label="Latitude"
                name="latitude"
                // Text, not number: a number input silently blanks a padded or
                // malformed paste, so the checks below would never see it
                inputMode="decimal"
                value={formData.latitude}
                onChange={handleInputChange}
                fullWidth
                placeholder="12.9724"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Coordinate format">
                        <IconButton
                          aria-label="Show latitude and longitude format"
                          edge="end"
                          onClick={() => setCoordinateHelpOpen(true)}
                        >
                          <HelpOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Longitude"
                name="longitude"
                inputMode="decimal"
                value={formData.longitude}
                onChange={handleInputChange}
                fullWidth
                placeholder="77.5806"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Coordinate format">
                        <IconButton
                          aria-label="Show latitude and longitude format"
                          edge="end"
                          onClick={() => setCoordinateHelpOpen(true)}
                        >
                          <HelpOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}><TextField label="Directions Link" name="directions_link" value={formData.directions_link} onChange={handleInputChange} fullWidth placeholder="https://maps.google.com/..." /></Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* CONTACT */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 'bold' }}>CONTACT & MEDIA</Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              {/* The selected country's code first, then the rest as a fallback.
                  Typeable: filters on either the code or the country name. */}
              <Autocomplete
                options={PHONE_CODE_OPTIONS(formData)}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.dial === value.dial}
                value={PHONE_CODE_OPTIONS(formData).find((o) => o.dial === formData.phone_code) || null}
                onChange={(event, selected) =>
                  setFormData((prev) => ({ ...prev, phone_code: selected ? selected.dial : "" }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Code" required fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Contact Number"
                name="contact"
                value={formData.contact}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={5}><TextField label="Email" name="email" type="email" value={formData.email} onChange={handleInputChange} fullWidth required /></Grid>

            {/* IMAGE LINKS */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Images</Typography>
              <FilePond
                files={imageFiles}
                onupdatefiles={setImageFiles}
                allowMultiple={true}
                acceptedFileTypes={['image/*']}
                labelIdle='Drag & Drop your images or <span class="filepond--label-action">Browse</span>'
                disabled={imageLinks.some(link => link.trim().length > 0)}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 2 }}>
                Upload files OR provide URLs below. Using one method disables the other.
              </Typography>

              {imageLinks.map((link, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                  <TextField
                    label={`Image Link URL ${index + 1}`}
                    value={link}
                    onChange={(e) => handleImageLinkChange(index, e.target.value)}
                    fullWidth
                    disabled={imageFiles.length > 0}
                  />
                  <IconButton onClick={() => handleRemoveImageLink(index)} color="error" disabled={imageFiles.length > 0}><DeleteIcon /></IconButton>
                </Box>
              ))}
              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddImageLink} size="small" disabled={imageFiles.length > 0}>Add Image Link</Button>

              {/* URL Previews if any */}
              {imagePreviewLinks.some(link => link.trim() !== "") && imageFiles.length === 0 && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {imagePreviewLinks.filter(l => l.trim() !== "").map((link, i) => (
                    <Box key={i} sx={{ p: 1, border: '1px dashed #ccc', borderRadius: 2, textAlign: 'center' }}>
                      <img
                        src={link}
                        alt={`Preview ${i + 1}`}
                        style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* OPERATING SCHEDULE */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 'bold' }}>OPERATING SCHEDULE</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {operatingSchedules.map((schedule, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 2, alignItems: 'center' }}>
                  <Grid item xs={5}>
                    <TextField
                      label="Operating Days"
                      value={schedule.days}
                      onChange={(e) => handleScheduleChange(index, 'days', e.target.value)}
                      placeholder="e.g., Mon - Sat"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Operating Hours"
                      value={schedule.hours}
                      onChange={(e) => handleScheduleChange(index, 'hours', e.target.value)}
                      placeholder="e.g., 10:00 - 21:00"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton onClick={() => handleRemoveSchedule(index)} color="error"><DeleteIcon /></IconButton>
                  </Grid>
                </Grid>
              ))}
              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddSchedule} size="small">Add Schedule</Button>
            </Grid>
          </Grid>

          {/* SETTINGS */}
          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <FormControlLabel control={<Switch checked={Boolean(formData.operational)} onChange={handleSwitchChange} name="operational" />} label="Operational (Active)" />
            <FormControlLabel control={<Switch checked={Boolean(formData.visible)} onChange={handleSwitchChange} name="visible" />} label="Visible" />
          </Box>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={() => navigate("/service/service-centre-master")}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Service Centre"}
            </Button>
          </Box>
        </form>
      </Paper>

      <Dialog open={coordinateHelpOpen} onClose={() => setCoordinateHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Expected Coordinate Format</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter latitude and longitude as decimal degree numbers.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            Format
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: "monospace", mb: 2 }}>
            latitude: -90 to 90, longitude: -180 to 180 with 4 decimal places.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            Examples
          </Typography>
          <Typography variant="body2">Latitude: 12.9724</Typography>
          <Typography variant="body2">Longitude: 77.5806</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Use negative values for south latitudes and west longitudes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCoordinateHelpOpen(false)}>Got it</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
